import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeature } from '../components/PremiumFeature';
import axios from 'axios';
import { 
    ArrowLeft, 
    Bookmark,
    Heart,
    Trash2,
    Sparkles,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { IconButton, PrimaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const SavedFavourites = () => {
    const navigate = useNavigate();
    const { canAccess, token } = useAuth();
    const { checkAccess } = usePremiumFeature();
    
    const [savedItems, setSavedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchFavourites = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/users/me/favourites`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedItems(response.data.favourites || []);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch favourites:', err);
            if (err.response?.status === 403) {
                setError('upgrade');
            } else {
                setError('Failed to load favourites');
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        // Always fetch favourites - no access check needed
        fetchFavourites();
    }, [fetchFavourites]);

    const handleRemove = async (id) => {
        try {
            await axios.delete(`${API}/api/users/me/favourites/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedItems(prev => prev.filter(item => item.id !== id));
            toast.success('Removed from favourites');
        } catch (err) {
            console.error('Failed to remove favourite:', err);
            toast.error('Failed to remove');
        }
    };

    // Format saved_at date
    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <IconButton 
                            onClick={() => navigate('/regulate')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-lg">Saved Favourites</Heading>
                        <div className="w-11" />
                    </Container>
                </header>
                <main className="max-w-xl mx-auto px-6 py-8 flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 text-[#1D6B6B] animate-spin" />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/regulate')}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg">Saved Favourites</Heading>
                    <div className="w-11" />
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {savedItems.length === 0 ? (
                    // Empty state
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center brand-fade-in">
                        <IconContainer variant="gold" size="xl" useGradient={true} className="mb-6">
                            <Bookmark className="w-8 h-8" />
                        </IconContainer>
                        <Heading as="h2" size="text-2xl" className="mb-3">
                            No favourites yet
                        </Heading>
                        <BodyText className="text-[#2A8585] max-w-sm mb-8">
                            When you find a reframe, grounding step, or script that resonates, save it here for easy access.
                        </BodyText>
                        <PrimaryButton
                            onClick={() => navigate('/regulate')}
                            size="lg"
                            data-testid="start-exploring-btn"
                        >
                            <Heart className="w-4 h-4 mr-2" />
                            Start exploring
                        </PrimaryButton>
                    </div>
                ) : (
                    // Saved items list
                    <div>
                        <div className="text-center mb-8 brand-fade-in">
                            <IconContainer variant="gold" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Bookmark className="w-6 h-6" />
                            </IconContainer>
                            <SmallText className="text-[#7D8E95]">
                                {savedItems.length} saved {savedItems.length === 1 ? 'item' : 'items'}
                            </SmallText>
                        </div>

                        <div className="space-y-4">
                            {savedItems.map((item, index) => (
                                <div className="glass-card rounded-2xl p-5"
                                    key={item.id}
                                    variant="elevated"
                                    padding="lg"
                                    className="brand-slide-up"
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    data-testid={`favourite-item-${item.id}`}
                                >
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="px-3 py-1 text-xs rounded-full bg-[#FFBB98] text-[#2A8585] font-medium">
                                                {item.category}
                                            </span>
                                            <SmallText className="text-[#7D8E95]">
                                                {formatDate(item.saved_at)}
                                            </SmallText>
                                        </div>
                                    </div>
                                    <BodyText className="text-[#1D6B6B] leading-relaxed mb-4">
                                        {item.content}
                                    </BodyText>
                                    <div className="flex justify-end">
                                        <TertiaryButton
                                            onClick={() => handleRemove(item.id)}
                                            className="text-[#FFBB98] hover:text-[#FFBB98]"
                                            data-testid={`remove-btn-${item.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 mr-1" />
                                            Remove
                                        </TertiaryButton>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 text-center">
                            <SmallText className="text-[#7D8E95]">
                                Your personal toolkit, always here when you need it.
                            </SmallText>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default SavedFavourites;
